import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Inject, Injectable, Injector } from '@angular/core';
import { Router } from '@angular/router';
import { environment } from '../../../environments/environment';
import { GuiMsjService } from '../shared/gui-msj.service';
import { GuiToolsService } from '../shared/gui-tools.service';
import { Response } from '../../interfaces/interface-bakend/response';
import { Register } from '../../interfaces/interface-frontend/register';
import Swal from 'sweetalert2';
import { User } from '../../interfaces/interface-bakend/seguridad/users/user.interface';
import { Observable, of, Subject, throwError } from 'rxjs';
import { Translators } from '../shared/translators.service';
import { StorageService } from '../shared/storage.service';
import { APP_CONFIG, IAppConfig } from '../../app.config/app.config';
import { promise } from 'protractor';
import { MensajesBackendService } from '@services/msj/mensajes-backend';
import { GecrosSyncService } from '@services/entidades/gecros-sync.service';
import { PopUpModalCustomService } from '@services/shared/popup-modal-custom.service';
import { UserEntreUserService } from '../shared/user-entre-user.service';

const URL_BACKEND = environment.urlBackend;

/**
 * Perfiles NO administrativos: entran a Inicio. El resto (Sede, Admin,
 * Auditoría, Sysadmin, Recupero) van al Panel Administrativo.
 */
const PERFILES_MORTALES = ['AFILIADO', 'GENERAL', 'LIMITADO'];

/**
 * Resultado de una validación de sesión contra el backend.
 * - 'valid': token y sesión server-side vigentes.
 * - 'invalid': sin token, o el backend lo rechazó (401 / sesión cerrada).
 * - 'unreachable': no se pudo verificar (red, 500, timeout). NO implica logout.
 */
export type SessionValidationResult = 'valid' | 'invalid' | 'unreachable';

@Injectable({
    providedIn: 'root',
})
export class AuthService {
    token: string = null;
    emailIngresado: string = null;
    idUsuario: string = null;
    usuario: User;
    idioma: '';
    aceptaTerminos: boolean;
    check: boolean;
    private checkTerminos$ = new Subject<Boolean>();

    /** Cache en memoria del usuario logueado (evita revalidar en cada acción). */
    private usuarioLogonCache: User = null;
    private usuarioLogonEnCurso: Promise<User> = null;
    /** Evita avisos/redirects duplicados cuando varias peticiones fallan juntas. */
    private authFailureHandled = false;

    private _userEntreUserService: UserEntreUserService;
    private _gecrosSyncService: GecrosSyncService;

    constructor(
        private router: Router,
        private httpClient: HttpClient,
        public guiMsjService: GuiMsjService,
        public guiToolsService: GuiToolsService,
        public translator: Translators,
        private storageService: StorageService,
        @Inject(APP_CONFIG) private iAppConfig: IAppConfig,
        private mensajesBackendService: MensajesBackendService,
        private modalCustom: PopUpModalCustomService,
        private injector: Injector
    ) {
        this.checkTerminos$ = new Subject();
    }

    getCustomBody(): any {
        const data = { data: '' };
        return data;
    }

    setCheck(acepta: boolean) {
        this.check = acepta;
        this.checkTerminos$.next(acepta);
    }

    getCheck$(): Observable<Boolean> {
        return this.checkTerminos$.asObservable();
    }

    async getIdiomaStorage() {
        await this.storageService.getIdioma().then((idioma: any) => {
            this.idioma = idioma;
            return;
        });
    }

    async validateTerms(): Promise<boolean> {
        const user = await this.getUserLogon();
        this.aceptaTerminos = user.aceptaTerminos;
        return this.aceptaTerminos;
    }

    /**
     * Chequeo síncrono y barato: ¿hay token guardado? Lo usa el AuthGuard para
     * decidir la navegación sin disparar HTTP en cada ruta.
     */
    hasToken(): boolean {
        return !!localStorage.getItem('signo-token');
    }

    /**
     * Valida la sesión contra el backend SIN efectos colaterales: no navega ni
     * limpia storage. Así la revalidación se puede correr solo al arranque
     * (bootstrap) y no en medio de un flujo (aprobar/rechazar).
     */
    async revalidarSesion(): Promise<SessionValidationResult> {
        await this.loadJwToken();

        if (!this.token) {
            return 'invalid';
        }

        try {
            const resp: any = await this.httpClient
                .get(`${URL_BACKEND}/auth/verificar`, {
                    headers: new HttpHeaders({ 'x-token': this.token }),
                })
                .toPromise();

            return resp && resp.ok ? 'valid' : 'invalid';
        } catch (error) {
            // 401 => sesión realmente inválida; el resto (red/500/timeout) no
            // debe expulsar al usuario.
            return this.esErrorDeSesion(error) ? 'invalid' : 'unreachable';
        }
    }

    /**
     * Compatibilidad con llamadas existentes. Valida y, si la sesión es
     * inválida, la cierra. Para no acoplar navegación usar revalidarSesion().
     */
    async validateToken(): Promise<boolean> {
        const resultado = await this.revalidarSesion();
        if (resultado === 'invalid') {
            this.handleAuthFailure();
            return false;
        }
        if (resultado === 'unreachable') {
            // No bloqueamos por un problema de red: decidimos por token local.
            return this.hasToken();
        }
        return true;
    }

    /**
     * Cierre de sesión por autenticación inválida (401 / sesión cerrada en otro
     * dispositivo). Se ejecuta UNA sola vez, aunque fallen varias peticiones en
     * paralelo, y limpia cachés para que un login posterior arranque limpio.
     *
     * Muestra el aviso en el modal propio (no SweetAlert) y navega al login
     * recién al confirmar, para que el mensaje no se pierda con el redirect.
     *
     * `codigo` es el mensaje del 401 del backend (auth-service*TOKEN_EXPIRED,
     * auth-service*SESSION_OUT, etc.). Si no llega, se usa un mensaje genérico.
     */
    handleAuthFailure(codigo?: string): void {
        if (this.authFailureHandled) {
            return;
        }
        this.authFailureHandled = true;

        this.usuarioLogonCache = null;
        this.usuarioLogonEnCurso = null;
        this.token = null;
        this.storageService.clearAllStorage();

        const aviso = this.mensajeSesion(codigo);

        this.modalCustom.abrirPopup({
            tipo: 'warning',
            titulo: aviso.titulo,
            mensaje: aviso.mensaje,
            textoBoton: 'Ir al login',
            sticky: true,
            accionConfirmar: () =>
                this.router.navigate(['/profile-authentication']),
        });
    }

    /** Extrae el código del body de un 401 (p. ej. 'auth-service*TOKEN_EXPIRED'). */
    codigoDeError(error: any): string {
        const body = error && error.error;
        if (!body) {
            return '';
        }
        if (typeof body === 'string') {
            return body;
        }
        const msg = body.message;
        return Array.isArray(msg) ? msg.join(' ') : msg || '';
    }

    /** Traduce el código del backend al texto del aviso de sesión. */
    private mensajeSesion(codigo?: string): {
        titulo: string;
        mensaje: string;
    } {
        const code = (codigo || '').toUpperCase();

        if (code.includes('TOKEN_EXPIRED')) {
            return {
                titulo: 'Sesión vencida',
                mensaje: 'Tu sesión venció. Por favor, ingresá nuevamente.',
            };
        }
        if (code.includes('EMAIL_NOT_VERIFIED')) {
            return {
                titulo: 'Cuenta sin verificar',
                mensaje:
                    'Tu cuenta todavía no está verificada. Por favor, contactá a administración.',
            };
        }
        if (code.includes('SESSION_OUT')) {
            return {
                titulo: 'Sesión cerrada',
                mensaje:
                    'Se inició sesión con tu usuario en otro dispositivo. Por favor, ingresá nuevamente.',
            };
        }
        if (code.includes('TOKEN_INVALID')) {
            return {
                titulo: 'Sesión inválida',
                mensaje:
                    'Tu sesión no es válida. Por favor, ingresá nuevamente.',
            };
        }

        return {
            titulo: 'Sesión cerrada',
            mensaje: 'Tu sesión se cerró. Por favor, ingresá nuevamente.',
        };
    }

    private esErrorDeSesion(error: any): boolean {
        return !!error && error.status === 401;
    }

    async validateTokenGoole(token: string): Promise<any> {
        if (!token) {
            this.router.navigateByUrl('/home');
            return null;
        }

        return new Promise<any>((resolve) => {
            const headers = new HttpHeaders({
                'x-token': token,
            });

            this.httpClient
                .get(`${URL_BACKEND}/auth/verificar`, { headers })
                .subscribe(
                    async (resp: any) => {
                        if (resp.ok) {
                            console.log('🟢 Token Google válido', resp);
                            resolve(resp.usuario); // 👈 CLAVE
                        } else {
                            this.router.navigateByUrl(
                                '/profile-authentication'
                            );
                            resolve(null);
                        }
                    },
                    () => {
                        this.storageService.clearAllStorage();
                        this.router.navigateByUrl('/profile-authentication');
                        resolve(null);
                    }
                );
        });
    }

    async getIdentificacion(): Promise<string> {
        const identificacion = localStorage.getItem('identificacion');

        if (identificacion === null) {
            this.storageService.clearAllStorage();
            await this.router.navigateByUrl('/profile-authentication');
            await this.guiMsjService.msjFormSubmit('tokenVencido');
        }

        return identificacion; // Devuelve 'null' si no hay identificación.
    }

    async setIdentificacion(identificacion: string) {
        this.emailIngresado = identificacion;
        localStorage.setItem('identificacion', identificacion);
    }

    async getEmail(): Promise<string> {
        return localStorage.getItem('email') || null;
    }

    async setEmail(email: string) {
        this.emailIngresado = email;
        localStorage.setItem('email', email);
    }

    async getJwToken(): Promise<string> {
        return localStorage.getItem('signo-token') || null;
    }

    async setJWToken(token: string) {
        this.token = token;
        this.usuarioLogonCache = null;
        this.usuarioLogonEnCurso = null;
        this.authFailureHandled = false;
        localStorage.setItem('signo-token', token);
    }

    async loadJwToken(): Promise<string> {
        this.token = localStorage.getItem('signo-token') || null;
        return this.token;
    }

    async getHeadersBearer(): Promise<HttpHeaders> {
        const token = await this.loadJwToken();
        return new HttpHeaders({
            'x-token': token || '',
            'Authorization': `Bearer ${token || ''}`,
        });
    }

    async setIdUsuario(idUsuario: string) {
        this.idUsuario = idUsuario;
        localStorage.setItem('signo-identificador', idUsuario);
    }

    async loadIdUsuario(): Promise<string> {
        this.idUsuario = localStorage.getItem('signo-identificador') || null;
        return this.idUsuario;
    }

    async newPassword(
        password: string,
        confirmPassword: string
    ): Promise<boolean> {
        await this.loadJwToken();

        return new Promise(async (resolve, reject) => {
            const data = {
                password,
                confirmPassword,
            };

            const headers = new HttpHeaders({
                'x-token': this.token,
            });

            this.httpClient
                .post<Response>(`${URL_BACKEND}/auth/update`, data, { headers })
                .subscribe(
                    (resp) => resolve(!!resp),
                    (error) => {
                        console.error(error);
                        reject(error);
                    }
                );
        });
    }

    // original
    // async getUserLogon(): Promise<User> {

    //   return new Promise(async resolve => {

    //     await this.loadJwToken();
    //     const headers = new HttpHeaders({
    //       'x-token': this.token
    //     });

    //     this.httpClient.get<Response>(`${URL_BACKEND}/auth/getUserLogon`, { headers })
    //       .subscribe(async resp => {
    //         if (resp) {
    //           // Validación del email
    //           const emailIngresado = await this.getEmail();
    //           const emailRespuesta = resp.response.email;
    //           const datosValido = this.validarEmail(emailIngresado, emailRespuesta);
    //           if (datosValido) {
    //             resolve(resp.response);
    //           } else {
    //             this.storageService.clearAllStorage();
    //             this.router.navigateByUrl('/profile-authentication');
    //             await this.guiMsjService.msjFormSubmit('datosInvalidosToken');
    //           }
    //         } else {

    //           this.router.navigateByUrl('/profile-authentication');
    //           resolve(resp.response);
    //         }
    //       }, (error => {
    //         this.guiMsjService.msjFormSubmit('errorTokenLogon');
    //         this.router.navigateByUrl('/profile-authentication');
    //       }));
    //   });
    // }

    /**
     * Devuelve el usuario logueado, con cache en memoria para no pegarle a
     * /users/findOne en cada acción (p. ej. al aprobar/rechazar). No navega ni
     * limpia storage: los errores se propagan para que el llamador decida.
     */
    async getUserLogon(): Promise<User> {
        if (this.usuarioLogonCache) {
            return this.usuarioLogonCache;
        }

        if (this.usuarioLogonEnCurso) {
            return this.usuarioLogonEnCurso;
        }

        const enCurso = this.fetchUserLogon();
        this.usuarioLogonEnCurso = enCurso;

        const limpiar = () => {
            if (this.usuarioLogonEnCurso === enCurso) {
                this.usuarioLogonEnCurso = null;
            }
        };
        enCurso.then(limpiar, limpiar);

        return enCurso;
    }

    private async fetchUserLogon(): Promise<User> {
        const token = await this.loadJwToken();
        const id: string = await this.loadIdUsuario();

        if (!token || !id) {
            throw new Error('Sin token o identificador de usuario');
        }

        try {
            const resp: any = await this.httpClient
                .get(`${URL_BACKEND}/users/findOne/${id}`, {
                    headers: new HttpHeaders({ 'x-token': token }),
                })
                .toPromise();

            if (!resp || !resp.response) {
                throw new Error('Respuesta vacía al obtener el usuario logueado');
            }

            const emailIngresado = await this.getEmail();
            const emailRespuesta = resp.response.email;

            if (!this.validarEmail(emailIngresado, emailRespuesta)) {
                this.handleAuthFailure();
                throw new Error('El usuario logueado no coincide con la sesión');
            }

            this.usuarioLogonCache = resp.response;
            return resp.response;
        } catch (error) {
            if (this.esErrorDeSesion(error)) {
                this.handleAuthFailure(this.codigoDeError(error));
            }
            throw error;
        }
    }

    // REGISTRAR NUEVO USUARIO
    async registerUser(data: Register): Promise<boolean> {
        return new Promise((resolve) => {
            /* LLAVES DE SEGURIDAD */
            const headers = new HttpHeaders({
                clientid: this.iAppConfig.clientid,
                clientsecret: this.iAppConfig.clientsecret,
            });

            this.httpClient
                .post(`${URL_BACKEND}/auth/register`, data, { headers })
                .subscribe(
                    async (resp: Response) => {
                        if (resp.response.ok) {
                            await this.guiMsjService.msjFormSubmit(
                                'createUserOk'
                            );
                            await this.storageService.setEmailActivar(
                                data.email
                            );
                            // this.router.navigate(['/activate-account']);
                            resolve(true);
                        } else {
                            const { msj, msj2 } =
                                this.mensajesBackendService.concatenarMensaje(
                                    String(resp.response.message)
                                );
                            this.guiMsjService.msjBackend(
                                msj ||
                                    'error inesperado, contacte administración',
                                msj2
                            );

                            // await this.guiMsjService.msjFormSubmit('createUserError');

                            resolve(false);
                        }
                    },
                    (error) => {
                        Swal.close();

                        if (error.error.error.error.code === 11000) {
                            this.guiMsjService.msjFormSubmit(
                                'duplicateAccount'
                            );
                        } else {
                            this.guiMsjService.msjFormSubmit('createUserError');
                        }

                        resolve(false);
                    }
                );
        });
    }

    // REGISTRAR NUEVO AFILIADO DESDE LA LISTA DE AFILIADOS (ADMINISTRACION)
    async registerAfiliado(data: Register): Promise<boolean> {
        return new Promise((resolve) => {
            /* LLAVES DE SEGURIDAD */
            const headers = new HttpHeaders({
                clientid: this.iAppConfig.clientid,
                clientsecret: this.iAppConfig.clientsecret,
            });

            this.httpClient
                .post(`${URL_BACKEND}/auth/register`, data, { headers })
                .subscribe(
                    async (resp: Response) => {
                        if (resp.response.ok) {
                            await this.guiMsjService.msjFormSubmit(
                                'createAfiliadoOk'
                            );
                            await this.storageService.setEmailActivar(
                                data.email
                            );
                            resolve(true);
                        } else {
                            await this.guiMsjService.msjFormSubmit(
                                'createUserError'
                            );

                            const { msj, msj2 } =
                                this.mensajesBackendService.concatenarMensaje(
                                    String(resp.response.message)
                                );
                            this.guiMsjService.msjBackend(
                                msj ||
                                    'error inesperado, contacte administración',
                                msj2
                            );

                            resolve(false);
                        }
                    },
                    (error) => {
                        Swal.close();
                        if (error.error.error.error.code === 11000) {
                            this.guiMsjService.msjFormSubmit(
                                'duplicateAccount'
                            );
                        } else {
                            this.guiMsjService.msjFormSubmit('createUserError');
                        }

                        resolve(false);
                    }
                );
        });
    }

    async registerManualUser(
        user: Register
    ): Promise<{ success: boolean; userId?: string }> {
        return new Promise((resolve) => {
            //LLAVES DE SEGURIDAD
            const headers = new HttpHeaders({
                clientid: this.iAppConfig.clientid,
                clientsecret: this.iAppConfig.clientsecret,
            });

            this.httpClient
                .post(`${URL_BACKEND}/auth/register`, user, { headers })
                .subscribe(
                    async (resp: Response) => {
                        if (resp.response['ok']) {
                            await this.guiMsjService.msjFormSubmit(
                                'createManulUserOk'
                            );
                            resolve({
                                success: true,
                                userId: resp.response._id,
                            });
                        } else {
                            const { msj, msj2 } =
                                this.mensajesBackendService.concatenarMensaje(
                                    String(resp.response.message)
                                );
                            this.guiMsjService.msjBackend(
                                msj ||
                                    'error inesperado, contacte administración',
                                msj2
                            );
                            resolve({ success: false });
                        }
                    },
                    (error) => {
                        if (!error['ok']) {
                            this.guiMsjService.msjFormSubmit(
                                'errorSendMailRegistroError'
                            );
                        } else if (error.error.error.error.code === 11000) {
                            this.guiMsjService.msjFormSubmit(
                                'duplicateAccount'
                            );
                        } else {
                            this.guiMsjService.msjFormSubmit('createUserError');
                        }
                        resolve({ success: false });
                    }
                );
        });
    }

    //clon de registerManualUser() para inscripcion
    async registerInscripcionOs(
        user: Register,
        suppressMessage = false
    ): Promise<{ success: boolean; userId?: string; error?: any; reused?: boolean; mensajeMostrado?: boolean }> {
        return new Promise((resolve) => {
            // LLAVES DE SEGURIDAD
            const headers = new HttpHeaders({
                clientid: this.iAppConfig.clientid,
                clientsecret: this.iAppConfig.clientsecret,
            });

            this.httpClient
                .post(`${URL_BACKEND}/auth/register`, user, { headers })
                .subscribe(
                    async (resp: Response) => {
                        if (resp.response['ok']) {
                            if (!suppressMessage) {
                                await this.guiMsjService.msjFormSubmit(
                                    'continuarDDJJ'
                                );
                            }
                            resolve({
                                success: true,
                                userId: resp.response._id,
                                reused: !!resp.response.reused,
                            });
                        } else {
                            // El mensaje lo muestra quien llama (inscripción O.S.
                            // resuelve el texto según el código del backend).
                            if (!suppressMessage) {
                                const { msj, msj2 } =
                                    this.mensajesBackendService.concatenarMensaje(
                                        String(resp.response.message)
                                    );
                                this.guiMsjService.msjBackend(
                                    msj ||
                                        'error inesperado, contacte administración',
                                    msj2
                                );
                            }
                            resolve({ success: false, error: resp.response });
                        }
                    },
                    (error) => {
                        // HttpErrorResponse: `error.status` es el HTTP status; el
                        // código 11000 de Mongo puede venir anidado en el body.
                        const status = error?.status;
                        const code = error?.error?.error?.code ?? error?.error?.code;

                        if (status === 409 || code === 11000) {
                            this.modalCustom.abrirPopup({
                                tipo: 'warning',
                                titulo: 'Cuenta ya registrada',
                                mensaje:
                                    'El DNI o el email ingresados ya se encuentran registrados. ' +
                                    'Verifique los datos o comuníquese con la obra social.',
                                textoBoton: 'Entendido',
                                ancho: '480px',
                            });
                        } else if (status === 0) {
                            this.modalCustom.abrirPopup({
                                tipo: 'error',
                                titulo: 'Sin conexión',
                                mensaje:
                                    'No pudimos comunicarnos con el servidor. ' +
                                    'Verifique su conexión a internet e intente nuevamente.',
                                textoBoton: 'Entendido',
                                ancho: '480px',
                            });
                        } else {
                            this.modalCustom.abrirPopup({
                                tipo: 'error',
                                titulo: 'No se pudo completar el registro',
                                mensaje:
                                    'Intente nuevamente en unos minutos. Si el inconveniente persiste, ' +
                                    'comuníquese con la obra social.',
                                textoBoton: 'Entendido',
                                ancho: '480px',
                            });
                        }
                        // `mensajeMostrado`: el cartel ya salió acá, el componente
                        // no debe abrir un segundo popup por el mismo error.
                        resolve({ success: false, error, mensajeMostrado: true });
                    }
                );
        });
    }

    // Función para validar y repetir la llamada hasta coincidir
    validarEmail(emailIngresado: string, emailRespuesta: string): boolean {
        return emailRespuesta === emailIngresado;
    }

    // ACCESO AL SITIO
    /**
     * Redirige según el perfil del usuario logueado: los administrativos
     * (Sede, Admin, Auditoría, Sysadmin, Recupero) al Panel Administrativo;
     * el resto (Afiliado/General/Limitado) a Inicio.
     */
    async redirigirSegunPerfil(userLogon?: User): Promise<void> {
        const usuario = userLogon || (await this.getUserLogon());
        const key = usuario?.perfil?.key;
        const esAdministrativo = !!key && !PERFILES_MORTALES.includes(key);
        const destino = esAdministrativo
            ? '/configurar-administracion'
            : '/';
        if (this.router.url !== destino) {
            this.router.navigate([destino]);
        }
    }

    /**
     * Misma regla que `redirigirSegunPerfil()`, copiada tal cual para reusarla
     * desde otros carteles/permisos: **sin perfil NO es administrativo** (cae
     * en `/`, igual que un afiliado). No reemplaza las líneas del redirect, que
     * quedan intactas.
     */
    esAdministrativo(usuario?: User | null): boolean {
        const key = usuario?.perfil?.key;
        return !!key && !PERFILES_MORTALES.includes(key);
    }

    async login(email: string, password: string): Promise<boolean> {
        this.storageService.clearAllStorage();

        const data = {
            email,
            password,
        };

        return new Promise(async (resolve) => {
            /* LLAVES DE SEGURIDAD */
            const headers = new HttpHeaders({
                clientid: this.iAppConfig.clientid,
                clientsecret: this.iAppConfig.clientsecret,
            });

            this.httpClient
                .post(`${URL_BACKEND}/auth/login`, data, { headers })
                .subscribe(async (resp: Response) => {
                    if (resp.response.ok) {
                        const emailRespuesta = resp.response.email;

                        // Validación del email
                        const datosValido = this.validarEmail(
                            email,
                            emailRespuesta
                        );

                        if (datosValido) {
                            this.setEmail(email);
                            await this.setJWToken(resp.response.token);
                            await this.setIdUsuario(resp.response._id);
                            await this.setIdentificacion(resp.response.identificacion);

                            // Obtener familiares y actualizar GECROS en segundo plano
                            this.userEntreUserService.findGrupoFamiliar(resp.response._id)
                                .then(familiares => {
                                    const familiaresData = (familiares || [])
                                        .map((f: any) => ({
                                            familiarId: f?.adicional?._id,
                                            documento: f?.adicional?.identificacion,
                                        }))
                                        .filter(f => f.familiarId && f.documento);

                                    this.gecrosSyncService.sincronizarTitularYFamiliares(
                                        resp.response._id,
                                        resp.response.identificacion,
                                        familiaresData
                                    );
                                })
                                .catch(err => console.error('Error obteniendo familiares:', err));

                            this.modalCustom.abrirPopup({
                                tipo: 'success',
                                titulo: 'Usuario logueado correctamente',
                                mostrarBoton: false,
                                tiempoAutoCierre: 2000,
                            });
                            await this.redirigirSegunPerfil();
                            resolve(true);
                        } else {
                            this.storageService.clearAllStorage();
                            this.router.navigate(['/profile-authentication']);
                            this.modalCustom.abrirPopup({
                                tipo: 'warning',
                                titulo: 'Por favor, inténtelo nuevamente.',
                                mensaje: 'Parece que su conexión a internet es lenta. Por favor, inténtelo de nuevo más tarde - Login',
                            });
                            resolve(false);
                        }
                    } else {
                        const { msj, msj2 } =
                            this.mensajesBackendService.concatenarMensaje(
                                String(resp.response.message)
                            );
                        this.modalCustom.abrirPopup({
                            tipo: 'warning',
                            titulo: msj || 'error inesperado, contacte administración',
                            mensaje: msj2 || '',
                        });

                        resolve(false);
                    }
                });
        });
    }

    // SALIR DEL SITIO
    async logout() {
        this.usuario = null;
        this.usuarioLogonCache = null;
        this.usuarioLogonEnCurso = null;
        this.authFailureHandled = false;
        this.storageService.clearAllStorage();
        this.router.navigate(['/profile-authentication']);
    }

    // CONFIRMAR CUENTA MEDIANTE PIN
    async confirmAccount(data): Promise<boolean> {
        return new Promise((resolve) => {
            /* LLAVES DE SEGURIDAD */
            const headers = new HttpHeaders({
                clientid: this.iAppConfig.clientid,
                clientsecret: this.iAppConfig.clientsecret,
            });

            this.httpClient
                .post(`${URL_BACKEND}/auth/confirmRegister`, data, { headers })
                .subscribe(
                    async (resp: Response) => {
                        if (resp.response.ok) {
                            // await this.setJWToken(resp.response.token);
                            this.router.navigateByUrl(
                                '/profile-authentication'
                            );
                            await this.guiMsjService.msjFormSubmit(
                                'activateAccount'
                            );
                            resolve(true);
                        }
                    },
                    async (error) => {
                        Swal.close();
                        await this.guiMsjService.msjFormSubmit(
                            'activateAccountError'
                        );
                        this.router.navigateByUrl('/activate-account');
                        resolve(false);
                    }
                );
        });
    }

    // RESETEAR PASSWORD DE CUENTA
    async resetPassword(email: string): Promise<boolean> {
        return new Promise<boolean>((resolve) => {
            /* LLAVES DE SEGURIDAD */
            const headers = new HttpHeaders({
                clientid: this.iAppConfig.clientid,
                clientsecret: this.iAppConfig.clientsecret,
            });

            this.httpClient
                .get<Response>(`${URL_BACKEND}/auth/passwordRecover/${email}`, {
                    headers,
                })
                .subscribe(async (resp: Response) => {
                    if (resp.response.ok) {
                        Swal.close();
                        await this.guiMsjService.msjFormSubmit('resetPassword');
                        this.router.navigateByUrl('/profile-authentication');
                        resolve(true);
                    } else {
                        Swal.close();
                        await this.guiMsjService.msjFormSubmit(
                            'resetPasswordError'
                        );
                        this.router.navigateByUrl('/reset-password');
                        resolve(false);
                    }
                });
        });
    }

    // REENVIAR PIN ACTIVACION DE CUENTA
    async resendPin(email: string): Promise<boolean> {
        return new Promise<boolean>((resolve) => {
            /* LLAVES DE SEGURIDAD */
            const headers = new HttpHeaders({
                clientid: this.iAppConfig.clientid,
                clientsecret: this.iAppConfig.clientsecret,
            });

            this.httpClient
                .get<Response>(
                    `${URL_BACKEND}/auth/pinRegisterRegenerate/${email}`,
                    { headers }
                )
                .subscribe(async (resp: Response) => {
                    if (resp.response.ok) {
                        Swal.close();

                        await this.guiMsjService.msjFormSubmit('pinResendOk');
                        this.router.navigateByUrl('/profile-authentication');
                        resolve(true);
                    } else {
                        Swal.close();
                        await this.guiMsjService.msjFormSubmit(
                            'pinResendError'
                        );
                        this.router.navigateByUrl('/profile-authentication');
                        resolve(false);
                    }
                });
        });
    }

    getGuestToken() {
        return new Promise(async (resolve) => {
            /* LLAVES DE SEGURIDAD */
            const headers = new HttpHeaders({
                clientid: this.iAppConfig.clientid,
                clientsecret: this.iAppConfig.clientsecret,
            });

            this.httpClient
                .get(`${URL_BACKEND}/auth/getGuestToken`, { headers })
                .subscribe(async (resp: Response) => {
                    if (resp.response.ok) {
                        await this.setJWToken(resp.response.token);
                        this.router.navigate(['/']);
                        Swal.close();
                        resolve(true);
                    } else {
                        Swal.close();
                        await this.guiMsjService.msjFormSubmit('loginError');
                        resolve(false);
                    }
                });
        });
    }

    esUsuarioInvitado(): Promise<boolean> {
        return new Promise(async (resolve) => {
            const usuario: User = await await this.getUserLogon();
            if (usuario.email === 'getGuestToken@gmail.com') {
                resolve(true);
            } else {
                resolve(false);
            }
        });
    }

    async googleLogin(idToken: string): Promise<any> {
        const headers = new HttpHeaders({
            clientid: this.iAppConfig.clientid,
            clientsecret: this.iAppConfig.clientsecret,
        });
        return new Promise((resolve) => {
            this.httpClient
                .post(
                    `${URL_BACKEND}/auth/google-login`,
                    { idToken },
                    { headers }
                )
                .subscribe(
                    async (resp: any) => {
                        if (resp.ok) {
                            await this.setJWToken(resp.token);
                            await this.setIdUsuario(resp._id);
                            await this.setEmail(resp.email);
                            resolve(resp);
                        } else {
                            resolve(resp); // ← aquí vuelven los códigos USER_NOT_FOUND o INVALID_TOKEN
                        }
                    },
                    (error) => resolve({ ok: false })
                );
        });
    }

    async verifyInternalToken(): Promise<any> {
        const headers = new HttpHeaders({
            clientid: this.iAppConfig.clientid,
            clientsecret: this.iAppConfig.clientsecret,
        });

        return new Promise((resolve) => {
            this.httpClient
                .get(`${URL_BACKEND}/auth/verificar`, { headers })
                .subscribe(
                    (resp: any) => resolve(resp),
                    () => resolve({ ok: false })
                );
        });
    }

    async verificarSesion(): Promise<any> {
        const headers = new HttpHeaders({
            'x-token': await this.loadJwToken(),
            clientid: this.iAppConfig.clientid,
            clientsecret: this.iAppConfig.clientsecret,
        });

        return new Promise((resolve, reject) => {
            this.httpClient
                .get(`${URL_BACKEND}/auth/verificar`, { headers })
                .subscribe(
                    (resp: any) => {
                        if (resp?.ok) {
                            resolve(resp);
                        } else {
                            reject(resp);
                        }
                    },
                    (err) => reject(err)
                );
        });
    }

    async hydrateSessionFromUser(usuario: any, token: string) {
        console.log('🧠 Hidratando sesión desde Google');

        await this.setJWToken(token);
        await this.setEmail(usuario.email);
        await this.setIdUsuario(usuario._id);
        await this.setIdentificacion(usuario.identificacion);

        // Obtener familiares y actualizar GECROS en segundo plano
        this.userEntreUserService.findGrupoFamiliar(usuario._id)
            .then(familiares => {
                const familiaresData = (familiares || [])
                    .map((f: any) => ({
                        familiarId: f?.adicional?._id,
                        documento: f?.adicional?.identificacion,
                    }))
                    .filter(f => f.familiarId && f.documento);

                this.gecrosSyncService.sincronizarTitularYFamiliares(
                    usuario._id,
                    usuario.identificacion,
                    familiaresData
                );
            })
            .catch(err => console.error('Error obteniendo familiares:', err));
    }

    private get userEntreUserService(): UserEntreUserService {
        if (!this._userEntreUserService) {
            this._userEntreUserService = this.injector.get(UserEntreUserService);
        }
        return this._userEntreUserService;
    }

    private get gecrosSyncService(): GecrosSyncService {
        if (!this._gecrosSyncService) {
            this._gecrosSyncService = this.injector.get(GecrosSyncService);
        }
        return this._gecrosSyncService;
    }

}
